import { Loader, Text, TextInput } from '@mantine/core';
import { useMyForm } from '../../../../../react-commons/hooks/useMyForm';
import { ResourceType } from '../../../resource/enumerations/ResourceType';
import {
    defaultProjectResource,
    ProjectMutationResourceSchema,
    type ProjectResource,
    useProjectActions,
} from '../../../resource/resources/artifacts/project/projectResource';
import { useProjectVersionActions } from '../../../resource/resources/artifacts/project/projectVersionResource';
import { useUserActions } from '../../../resource/resources/persistables/user/userResource';
import { useUserId } from '../../authentication/useUserId';
import { ProjectContent } from '../../page-components/project/ProjectContent';
import { Form_EditableViewWrapper } from '../../reusable-components/form-components/Form_EditableViewWrapper';
import { ResourceValidationTitleBar } from '../../reusable-components/validation/ResourceValidationTitleBar';
import { ProjectSettingsButton } from './ProjectSettingsButton';

export const ProjectsComponentSelectedOverview = ({ project }: { project: ProjectResource | undefined }) => {
    const userId = useUserId('');

    const userActions = useUserActions();
    const userSystemRights = userActions.useGetUserEffectiveRightsOfUser(userId, project?.id);
    const projectVersionActions = useProjectVersionActions();
    const projectActions = useProjectActions();

    const projectVersionsQuery = projectVersionActions.useGetAllResources(project?.id);
    const updateProject = projectActions.useUpdateResource();

    const getProjectVersion = () => (projectVersionsQuery.data?.length ? projectVersionsQuery.data.at(-1) : undefined);
    const isProcessAdmin = userSystemRights.data?.isProcessAdmin;

    const { form, formNames, onSubmit } = useMyForm({
        schema: ProjectMutationResourceSchema,
        defaultValues: defaultProjectResource,
        submitHandler: async (data) => {
            await updateProject.mutateAsync({
                resource: data,
                id: data.id,
            });
        },
        reactiveValues: project,
    });

    if (!project) {
        return <TextInput leftSection={<Loader size="sm" />} value={`Loading Project…`} readOnly />;
    }

    return (
        <Form_EditableViewWrapper form={form} onSubmit={onSubmit}>
            <ResourceValidationTitleBar
                metaDataInfo={project}
                versionMetaDataInfo={getProjectVersion()}
                resourceType={[ResourceType.PROJECT, ResourceType.PROJECT_VERSION]}
                customButtons={<ProjectSettingsButton projectId={project.id} />}
            >
                <Text>Project</Text>
            </ResourceValidationTitleBar>
            <ProjectContent
                id={project.id}
                versionId={getProjectVersion()?.id}
                versionInfo={getProjectVersion()?.versionInfo}
                form={form}
                formNames={formNames}
                readOnly={!isProcessAdmin}
                hideName={false}
            />
        </Form_EditableViewWrapper>
    );
};
